import { StrictMode, Suspense, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider } from 'antd';
import { I18nextProvider, useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import i18n from './i18n';
import koKR from 'antd/lib/locale/ko_KR';
import enUS from 'antd/lib/locale/en_US';
import zhCN from 'antd/lib/locale/zh_CN';
import moment from 'moment';
import 'moment/locale/ko';
import 'moment/locale/zh-cn';
import 'moment/locale/en-gb';
import './index.css';
// import 'antd/dist/antd.css';
import 'antd/dist/antd.less';
import '@fontsource-variable/noto-sans-kr';
import '@fontsource-variable/noto-sans-sc';
import '@styles/vendors/bootstrap-utilities.scss';
import '@styles/app.scss';
import '@styles/utilities/colors.less';
import './App.css';
import App from './App.jsx';

if (import.meta.env.VITE_APP_ENV === 'PRODUCTION') {
  // Sentry Configuration
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DNS,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 0.8, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracingOrigins: [/^https:\/\/carbon\.dataflare\.net\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.8, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

// moment.locale('ko');
const antdLocales = { ko: koKR, en: enUS, zh: zhCN };

export function Root() {
  const { i18n: i18nextInstance } = useTranslation();
  const [_, setAntdLocale] = useState(antdLocales[i18nextInstance.language] || enUS);

  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setAntdLocale(antdLocales[lng] || enUS);
      moment.locale(lng === 'zh' ? 'zh-cn' : lng);
    };

    handleLanguageChanged(i18nextInstance.language);
    i18nextInstance.on('languageChanged', handleLanguageChanged);

    return () => {
      i18nextInstance.off('languageChanged', handleLanguageChanged);
    };
  }, [i18nextInstance]);

  return (
    <ConfigProvider
      locale={i18n}
      theme={{
        token: {
          fontFamily: `"Noto Sans SC Variable", "Noto Sans KR Variable", sans-serif`,
        },
      }}
    >
      <App />
    </ConfigProvider>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <Suspense fallback={<div>Loading...</div>}>
        <Root />
      </Suspense>
    </I18nextProvider>
  </StrictMode>
);
